import {
    Component,
    OnInit
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators
} from '@angular/forms';
import { IMaskDirective } from 'angular-imask';
import { FactoryArg } from 'imask/masked/factory';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import {
    DynamicDialogConfig,
    DynamicDialogRef
} from 'primeng/dynamicdialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { finalize } from 'rxjs';

import {
    AppLoaderComponent,
    VerifiableInput
} from '@/components';
import { UserRole } from '@/enums';
import { Option } from '@/interfaces';
import { UsersService } from '@/service';
import { ValidationService } from '@/service/validation.service';

@Component({
    selector: 'app-user-create',
    templateUrl: './create.component.html',
    standalone: true,
    imports: [
        InputText,
        ReactiveFormsModule,
        Button,
        Select,
        VerifiableInput,
        AppLoaderComponent,
        IMaskDirective
    ]
})
export class UserCreateComponent implements OnInit {
    form!: FormGroup;
    isEdit = false;
    roleOptions: Option[] = Object.values(UserRole).map(value => ({ value, label: value }));
    loading = false;
    mask: FactoryArg = { mask: '(000) 000-0000' };

    constructor(
        private fb: FormBuilder,
        private validationService: ValidationService,
        private usersService: UsersService,
        private messageService: MessageService,
        public ref: DynamicDialogRef,
        public config: DynamicDialogConfig
    ) {
    }

    ngOnInit(): void {
        const user = this.config.data;
        this.isEdit = !!user?.id;
        this.form = this.fb.group({
            name: [user?.name || null, Validators.required],
            email: [user?.email || null, [Validators.required, Validators.email]],
            role: [user?.role || null, Validators.required],
            phone: [user?.phone || null]
        });
        if (this.isEdit) {
            this.form.get('email')?.disable();
        }
    }

    submit(): void {
        this.validate();
        if (!this.validationService.checkValidity('create-user')) {
            return;
        }

        this.loading = true;
        const payload = this.form.getRawValue();
        const request$ = this.config.data?.id
            ? this.usersService.edit(this.config.data.id, payload)
            : this.usersService.create(payload);

        request$
            .pipe(
                finalize(() => {
                    this.loading = false;
                })
            )
            .subscribe({
                next: (result) => {
                    this.ref.close(result ?? payload);
                },
                error: (err) => {
                    this.loading = false;
                    if (err.error?.error) {
                        this.messageService.add({
                            severity: 'error',
                            detail: err.error?.error
                        });
                    }
                }
            });
    }

    cancel(): void {
        this.ref.close(null);
    }

    validate(): void {
        this.validationService.clearAllErrors();

        const name = this.form.get('name')?.value;
        const role = this.form.get('role')?.value;
        const email = this.form.get('email')?.value;

        if (!name) {
            this.validationService.setError('create-user', 'name', 'Name is required.');
        } else if (name.length > 100) {
            this.validationService.setError('create-user', 'name', 'Name must be at most 100 characters.');
        }

        if (!role) {
            this.validationService.setError('create-user', 'role', 'Role is required.');
        }

        if (!email) {
            this.validationService.setError('create-user', 'email', 'Email is required.');
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                this.validationService.setError('create-user', 'email', 'Email is invalid.');
            } else if (email.length > 100) {
                this.validationService.setError('create-user', 'email', 'Email must be at most 100 characters.');
            }
        }
    }
}
